import { resolveSchedulingLink } from "@shared/schema";
import { AlertTriangle, Check, Copy, Sparkles } from "lucide-react";
import { useEffect, useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  SearchableSelect,
  type SearchableSelectOptionGroup,
} from "@/components/ui/searchable-select";
import { useToast } from "@/hooks/use-toast";
import type { AssignmentOption, PersonOption } from "@/types/move-application";

export interface InterviewerPickerProps {
  assignmentOptions: AssignmentOption[];
  people?: PersonOption[];
  isLoading?: boolean;

  interviewerId: string;
  setInterviewerId: (id: string) => void;
  selectedGroupId: string | null;
  setSelectedGroupId: (id: string | null) => void;
  assignmentMode: "automatic" | "manual";
  setAssignmentMode: (mode: "automatic" | "manual") => void;

  /** Duration used to resolve the correct scheduling link */
  durationMinutes?: number;

  /** Show "Copiar link do entrevistador" button when a link is available */
  showCopyLink?: boolean;

  /** Show "Enviar email ao candidato" checkbox */
  showSendEmail?: boolean;
  sendEmail?: boolean;
  onSendEmailChange?: (checked: boolean) => void;

  /** Called when selection changes — exposes derived state so parent doesn't need to recompute */
  onSelectionChange?: (info: {
    selectedOption: AssignmentOption | undefined;
    resolvedLink: string | null;
    hasNoSchedulingLink: boolean;
  }) => void;
}

/** Resolves the currently selected option from state. */
export function useSelectedOption(
  assignmentOptions: AssignmentOption[],
  people: PersonOption[] | undefined,
  interviewerId: string,
  selectedGroupId: string | null,
): AssignmentOption | undefined {
  return useMemo(() => {
    if (selectedGroupId) {
      return assignmentOptions.find((o) => o.type === "group" && o.id === selectedGroupId);
    }
    if (interviewerId) {
      const fromStage = assignmentOptions.find(
        (o) => o.type === "individual" && o.id === interviewerId,
      );
      if (fromStage) return fromStage;
      const person = people?.find((p) => p.id === interviewerId);
      if (person) {
        return {
          type: "individual" as const,
          id: interviewerId,
          label: person.fullName,
          interviewCount: 0,
          schedulingLinks: person.schedulingLinks,
          email: person.corporateEmail,
        };
      }
    }
    return undefined;
  }, [selectedGroupId, interviewerId, assignmentOptions, people]);
}

/** Resolves the scheduling link for the selected option. */
export function useResolvedSchedulingLink(
  selectedOption: AssignmentOption | undefined,
  durationMinutes: number,
): string | null {
  return useMemo(() => {
    if (!selectedOption) return null;
    if (selectedOption.type === "group") return selectedOption.schedulingLink || null;
    return resolveSchedulingLink(selectedOption.schedulingLinks || [], durationMinutes);
  }, [selectedOption, durationMinutes]);
}

/** Selects the best interviewer (fewest interviews) from the options. */
export function selectBestInterviewer(
  assignmentOptions: AssignmentOption[],
  setInterviewerId: (id: string) => void,
  setSelectedGroupId: (id: string | null) => void,
) {
  if (assignmentOptions.length === 0) return;
  const best = assignmentOptions.reduce(
    (min, curr) => (curr.interviewCount < min.interviewCount ? curr : min),
    assignmentOptions[0],
  );
  if (best.type === "group") {
    setSelectedGroupId(best.id);
    setInterviewerId("");
  } else {
    setSelectedGroupId(null);
    setInterviewerId(best.id);
  }
}

function buildInterviewerSelectProps(
  assignmentOptions: AssignmentOption[],
  people: PersonOption[] | undefined,
  hasStageInterviewers: boolean,
) {
  if (!hasStageInterviewers) {
    return {
      options: (people || []).map((person) => ({
        value: person.id,
        label: person.fullName,
      })),
    };
  }
  const stageOptions = assignmentOptions.map((opt) => ({
    value: opt.type === "group" ? `group:${opt.id}` : opt.id,
    label: `${opt.type === "group" ? "👥 " : ""}${opt.label} (${opt.interviewCount} entrevistas)`,
  }));
  if (!people || people.length === 0) {
    return { options: stageOptions };
  }
  const stageIds = new Set(
    assignmentOptions.filter((o) => o.type === "individual").map((o) => o.id),
  );
  const extraPeople = people
    .filter((p) => !stageIds.has(p.id))
    .map((person) => ({ value: person.id, label: person.fullName }));
  const groups: SearchableSelectOptionGroup[] = [
    { heading: "Entrevistadores e grupos da etapa", options: stageOptions },
    ...(extraPeople.length > 0
      ? [{ heading: "Todos os colaboradores", options: extraPeople }]
      : []),
  ];
  return { groups };
}

/**
 * Reusable interviewer/group picker.
 *
 * Includes: auto/manual radio, SearchableSelect with grouped options,
 * selected interviewer preview, "no scheduling link" warning,
 * optional copy-link button, and optional send-email checkbox.
 */
export function InterviewerPicker({
  assignmentOptions,
  people,
  isLoading = false,
  interviewerId,
  setInterviewerId,
  selectedGroupId,
  setSelectedGroupId,
  assignmentMode,
  setAssignmentMode,
  durationMinutes = 60,
  showCopyLink = false,
  showSendEmail = false,
  sendEmail,
  onSendEmailChange,
  onSelectionChange,
}: InterviewerPickerProps) {
  const pickerId = useId();
  const { toast } = useToast();
  const [copiedLink, setCopiedLink] = useState(false);
  const hasStageInterviewers = assignmentOptions.length > 0;

  const selectedOption = useSelectedOption(
    assignmentOptions,
    people,
    interviewerId,
    selectedGroupId,
  );
  const resolvedLink = useResolvedSchedulingLink(selectedOption, durationMinutes);
  const hasNoSchedulingLink = !!selectedOption && !resolvedLink;

  // Notify parent of derived state changes
  useEffect(() => {
    onSelectionChange?.({ selectedOption, resolvedLink, hasNoSchedulingLink });
  }, [selectedOption, resolvedLink, hasNoSchedulingLink, onSelectionChange]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
          <span className="ml-2 text-sm text-muted-foreground">Carregando entrevistadores...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {hasStageInterviewers && (
        <div className="space-y-3">
          <Label>Designar entrevistador</Label>
          <RadioGroup
            value={assignmentMode}
            onValueChange={(val) => {
              const mode = val as "automatic" | "manual";
              setAssignmentMode(mode);
              if (mode === "automatic") {
                selectBestInterviewer(assignmentOptions, setInterviewerId, setSelectedGroupId);
              } else {
                setInterviewerId("");
                setSelectedGroupId(null);
              }
            }}
            className="flex flex-col gap-3"
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem
                value="automatic"
                id={`${pickerId}-auto`}
                data-testid="radio-automatic"
              />
              <Label
                htmlFor={`${pickerId}-auto`}
                className="flex items-center gap-2 font-normal cursor-pointer"
              >
                <Sparkles className="w-4 h-4 text-primary" />
                Automático (quem tem menos entrevistas)
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="manual" id={`${pickerId}-manual`} data-testid="radio-manual" />
              <Label htmlFor={`${pickerId}-manual`} className="font-normal cursor-pointer">
                Escolher manualmente
              </Label>
            </div>
          </RadioGroup>
        </div>
      )}

      {hasStageInterviewers && assignmentMode === "automatic" && selectedOption && (
        <div className="bg-muted/50 rounded-md p-3 space-y-2">
          <p className="text-sm font-medium">
            {selectedOption.type === "group" ? "Grupo selecionado" : "Entrevistador selecionado"}:{" "}
            {selectedOption.label}
          </p>
          <p className="text-xs text-muted-foreground">
            {selectedOption.interviewCount} entrevistas neste processo
          </p>
        </div>
      )}

      {(assignmentMode === "manual" || !hasStageInterviewers) && (
        <div className="space-y-2">
          <Label htmlFor="interviewer">Entrevistador / Grupo *</Label>
          <SearchableSelect
            value={selectedGroupId ? `group:${selectedGroupId}` : interviewerId}
            onValueChange={(val) => {
              if (val.startsWith("group:")) {
                setSelectedGroupId(val.replace("group:", ""));
                setInterviewerId("");
              } else {
                setSelectedGroupId(null);
                setInterviewerId(val);
              }
            }}
            {...buildInterviewerSelectProps(assignmentOptions, people, hasStageInterviewers)}
            preserveOrder
            placeholder="Selecione o entrevistador ou grupo"
            triggerClassName={!interviewerId && !selectedGroupId ? "border-destructive" : ""}
            data-testid="select-interviewer"
          />
          {!interviewerId && !selectedGroupId && (
            <p className="text-xs text-destructive">
              Selecione um entrevistador ou grupo para continuar
            </p>
          )}
        </div>
      )}

      {hasNoSchedulingLink && (
        <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 p-2 rounded-md">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>
            Este entrevistador não possui link de agendamento configurado. O email não pode ser
            enviado.
          </span>
        </div>
      )}

      {/* Copy scheduling link button */}
      {showCopyLink && resolvedLink && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          data-testid="button-copy-interviewer-link"
          onClick={() => {
            navigator.clipboard.writeText(resolvedLink);
            setCopiedLink(true);
            toast({
              title: "Link copiado!",
              description: "Link do entrevistador copiado para a área de transferência.",
            });
            setTimeout(() => setCopiedLink(false), 2000);
          }}
        >
          {copiedLink ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
          {copiedLink ? "Link copiado!" : "Copiar link do entrevistador"}
        </Button>
      )}

      {/* Send email checkbox */}
      {showSendEmail && selectedOption && (
        <div className="flex items-center gap-2">
          <Checkbox
            id={`${pickerId}-send-email`}
            checked={hasNoSchedulingLink ? false : (sendEmail ?? false)}
            onCheckedChange={(checked) => onSendEmailChange?.(!!checked)}
            disabled={!!hasNoSchedulingLink}
            data-testid="checkbox-send-email"
          />
          <Label
            htmlFor={`${pickerId}-send-email`}
            className={`font-normal cursor-pointer text-sm ${hasNoSchedulingLink ? "text-muted-foreground" : ""}`}
          >
            Enviar email ao candidato com link de agendamento
          </Label>
        </div>
      )}
    </div>
  );
}
